import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-config';
import axios from '../utils/axios-config';
import { dkgOptions } from '../config/dkg-options';
import { checkMintStatus } from '../utils/minting/check-mint-status';

export function useForum() {
  const queryClient = useQueryClient();

  // Get all discussions
  const useGetAllDiscussions = (filters = {}) => {
    return useQuery({
      queryKey: ['forum', 'discussion', filters],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.FORUM.DISCUSSIONS.GET_ALL,
          {
            params: filters,
          }
        );
        const discussions =
          response.data && Array.isArray(response.data.discussions)
            ? response.data.discussions
            : [];
        // Debug: log all discussion slugs before filtering
        if (Array.isArray(discussions)) {
        }
        let filteredDiscussions = discussions;
        // Apply client-side filters if needed
        if (filters.category) {
          filteredDiscussions = filteredDiscussions.filter(
            (discussion) =>
              discussion.category &&
              discussion.category.toLowerCase() ===
                filters.category.toLowerCase()
          );
        }
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filteredDiscussions = filteredDiscussions.filter(
            (discussion) =>
              (discussion.title &&
                discussion.title.toLowerCase().includes(searchLower)) ||
              (discussion.text &&
                discussion.text.toLowerCase().includes(searchLower))
          );
        }
        return filteredDiscussions;
      },
      staleTime: 0, // Always refetch on filter change
    });
  };

  // Get discussion by OID
  const useGetDiscussionByOid = (discussionOid) => {
    return useQuery({
      queryKey: ['forum', 'discussion', discussionOid],
      queryFn: async () => {
        try {
          const response = await axios.get(
            API_ENDPOINTS.FORUM.DISCUSSIONS.GET_BY_OID(discussionOid)
          );
          return response.data.discussion || null;
        } catch (error) {
          return null;
        }
      },
      enabled: !!discussionOid,
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  };

  // Get comments by discussion OID
  const useGetCommentsByDiscussion = (discussionOid) => {
    return useQuery({
      queryKey: ['forum', 'comments', discussionOid],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.FORUM.COMMENTS.GET_BY_DISCUSSION(discussionOid)
        );
        return response.data;
      },
      enabled: !!discussionOid,
    });
  };

  // Get all categories
  const useGetCategories = () => {
    return useQuery({
      queryKey: ['forum', 'categories'],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.FORUM.CATEGORIES.GET_ALL
        );
        return response.data;
      },
    });
  };

  const useGetCategoryBySlug = (slug) => {
    return useQuery({
      queryKey: ['forum', 'category', slug],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.FORUM.CATEGORIES.GET_BY_SLUG(slug)
        );
        return response.data;
      },
      enabled: !!slug,
    });
  };

  // Create discussion mutation
  const createDiscussionMutation = useMutation({
    mutationFn: async (discussionData) => {
      const response = await axios.post(
        API_ENDPOINTS.FORUM.DISCUSSIONS.CREATE,
        {
          ...discussionData,
          options: dkgOptions,
        }
      );
      // Check for jobID and poll mint status (same as useCreateArticle)
      if (response.data && response.data.success && response.data.jobID) {
        const mintStatus = await checkMintStatus(
          response.data.jobID,
          4000, // 4 seconds
          250 // 6 minutes
        );
        if (!mintStatus || !mintStatus.success) {
          throw new Error(
            mintStatus?.error ||
              'Failed to mint discussion NFT. Please try again.'
          );
        }
        return {
          ...response.data,
          mintStatus: mintStatus.state,
          mintSuccess: mintStatus.success,
          error: mintStatus.error || null,
        };
      } else {
        throw new Error('Failed to create discussion. No job ID returned.');
      }
    },
    onSuccess: () => {
      // Invalidate all forum discussion queries to refresh lists
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussions'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });
      // Invalidate user stats so profile shows updated discussion count immediately
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async (commentData) => {
      const response = await axios.post(
        API_ENDPOINTS.FORUM.COMMENTS.ADD,
        commentData
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['forum', 'comments', data.discussionId],
      });
      // Invalidate user stats so profile shows updated comment count
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  // Upvote interaction (optimistic update)
  const upvoteInteraction = useMutation({
    mutationFn: async ({ discussionId, uOID, creator }) => {
      try {
        const response = await axios.post(
          API_ENDPOINTS.FORUM.DISCUSSIONS.UPVOTE(),
          {
            sOID: discussionId,
            uOID,
            creator,
            interactionType: 'upvote',
          }
        );
        return response.data;
      } catch (error) {
        console.error('[Upvote Error]', {
          discussionId,
          uOID,
          creator,
          error: error.response?.data || error.message,
          status: error.response?.status,
        });
        throw error;
      }
    },
    onMutate: async ({ discussionId }) => {
      await queryClient.cancelQueries();
      // Optimistically update single discussion
      const previousDiscussion = queryClient.getQueryData([
        'forum',
        'discussion',
        discussionId,
      ]);
      if (previousDiscussion) {
        queryClient.setQueryData(['forum', 'discussion', discussionId], {
          ...previousDiscussion,
          upvotes: (previousDiscussion.upvotes || 0) + 1,
        });
      }
      // Optimistically update all discussions lists (with any filters)
      const queries = queryClient.getQueriesData({
        queryKey: ['forum', 'discussion'],
      });
      const previousDiscussionsByKey = {};
      queries.forEach(([key, previousDiscussions]) => {
        if (Array.isArray(previousDiscussions)) {
          previousDiscussionsByKey[JSON.stringify(key)] = previousDiscussions;
          queryClient.setQueryData(
            key,
            previousDiscussions.map((d) =>
              d.oid === discussionId
                ? { ...d, upvotes: (d.upvotes || 0) + 1 }
                : d
            )
          );
        }
      });
      return { previousDiscussion, previousDiscussionsByKey };
    },
    onError: (err, variables, context) => {
      if (context?.previousDiscussion) {
        queryClient.setQueryData(
          ['forum', 'discussion', variables.discussionId],
          context.previousDiscussion
        );
      }
      if (context?.previousDiscussionsByKey) {
        Object.entries(context.previousDiscussionsByKey).forEach(
          ([key, data]) => {
            queryClient.setQueryData(JSON.parse(key), data);
          }
        );
      }
    },
    onSettled: (data, error, variables) => {
      // Get discussion data to find the creator
      const discussion = queryClient.getQueryData([
        'forum',
        'discussion',
        variables.discussionId,
      ]);

      queryClient.invalidateQueries({
        queryKey: ['forum', 'discussion', variables.discussionId],
      });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });

      // Invalidate all user queries to update stats (upvotes, downvotes, reputation)
      queryClient.invalidateQueries({ queryKey: ['user'] });

      // Specifically invalidate the discussion creator's user data (for stats received)
      if (discussion?.creator) {
        queryClient.invalidateQueries({
          queryKey: ['user', discussion.creator],
        });
      }

      // Invalidate the upvoter's interactions-given stats
      if (variables.uOID) {
        queryClient.invalidateQueries({
          queryKey: ['user', variables.uOID, 'interactions-given'],
        });
      }
    },
  });

  // Downvote interaction (optimistic update)
  const downvoteInteraction = useMutation({
    mutationFn: async ({ discussionId, uOID, creator }) => {
      const response = await axios.post(
        API_ENDPOINTS.FORUM.DISCUSSIONS.DOWNVOTE(),
        {
          sOID: discussionId,
          uOID,
          creator,
          interactionType: 'downvote',
        }
      );
      return response.data;
    },
    onMutate: async ({ discussionId }) => {
      await queryClient.cancelQueries();
      // Optimistically update single discussion
      const previousDiscussion = queryClient.getQueryData([
        'forum',
        'discussion',
        discussionId,
      ]);
      if (previousDiscussion) {
        queryClient.setQueryData(['forum', 'discussion', discussionId], {
          ...previousDiscussion,
          downvotes: (previousDiscussion.downvotes || 0) + 1,
        });
      }
      // Optimistically update all discussions lists (with any filters)
      const queries = queryClient.getQueriesData({
        queryKey: ['forum', 'discussion'],
      });
      const previousDiscussionsByKey = {};
      queries.forEach(([key, previousDiscussions]) => {
        if (Array.isArray(previousDiscussions)) {
          previousDiscussionsByKey[JSON.stringify(key)] = previousDiscussions;
          queryClient.setQueryData(
            key,
            previousDiscussions.map((d) =>
              d.oid === discussionId
                ? { ...d, downvotes: (d.downvotes || 0) + 1 }
                : d
            )
          );
        }
      });
      return { previousDiscussion, previousDiscussionsByKey };
    },
    onError: (err, variables, context) => {
      if (context?.previousDiscussion) {
        queryClient.setQueryData(
          ['forum', 'discussion', variables.discussionId],
          context.previousDiscussion
        );
      }
      if (context?.previousDiscussionsByKey) {
        Object.entries(context.previousDiscussionsByKey).forEach(
          ([key, data]) => {
            queryClient.setQueryData(JSON.parse(key), data);
          }
        );
      }
    },
    onSettled: (data, error, variables) => {
      // Get discussion data to find the creator
      const discussion = queryClient.getQueryData([
        'forum',
        'discussion',
        variables.discussionId,
      ]);

      queryClient.invalidateQueries({
        queryKey: ['forum', 'discussion', variables.discussionId],
      });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });

      // Invalidate all user queries to update stats (upvotes, downvotes, reputation)
      queryClient.invalidateQueries({ queryKey: ['user'] });

      // Specifically invalidate the discussion creator's user data (for stats received)
      if (discussion?.creator) {
        queryClient.invalidateQueries({
          queryKey: ['user', discussion.creator],
        });
      }

      // Invalidate the downvoter's interactions-given stats
      if (variables.uOID) {
        queryClient.invalidateQueries({
          queryKey: ['user', variables.uOID, 'interactions-given'],
        });
      }
    },
  });

  const useGetDiscussionsByCategory = (categoryId) => {
    return useQuery({
      queryKey: ['forum', 'discussions', 'category', categoryId],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.FORUM.DISCUSSIONS.GET_BY_CATEGORY(categoryId)
        );
        return response.data;
      },
      enabled: !!categoryId,
    });
  };

  const useGetTrendingDiscussions = () => {
    return useQuery({
      queryKey: ['forum', 'discussions', 'trending'],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.FORUM.DISCUSSIONS.GET_TRENDING
        );
        return response.data;
      },
    });
  };

  // Freeze discussion mutation
  const freezeDiscussion = useMutation({
    mutationFn: async ({ discussionOid }) => {
      const response = await axios.put(
        API_ENDPOINTS.FORUM.DISCUSSIONS.FREEZE_DISCUSSION,
        { discussionOid }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussions'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });
    },
  });

  // Unfreeze discussion mutation
  const unfreezeDiscussion = useMutation({
    mutationFn: async ({ discussionOid }) => {
      const response = await axios.put(
        API_ENDPOINTS.FORUM.DISCUSSIONS.UNFREEZE_DISCUSSION,
        { discussionOid }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussions'] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'discussion'] });
    },
  });

  // Add view mutation - invalidate user activity to update recent activity list
  const addView = useMutation({
    mutationFn: async ({ discussionOid, userWallet, metadata }) => {
      const response = await axios.post(
        API_ENDPOINTS.FORUM.DISCUSSIONS.ADD_VIEW,
        { discussionOid, userWallet, metadata }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Only invalidate userActivity query so Recent Activity updates
      // Don't invalidate discussion queries to prevent infinite re-render loops
      if (variables.userWallet) {
        queryClient.invalidateQueries({
          queryKey: ['userActivity', variables.userWallet],
        });
        // Invalidate view stats so profile updates immediately
        queryClient.invalidateQueries({
          queryKey: ['user', variables.userWallet, 'viewStats'],
        });
        // Invalidate content view status so UI updates
        queryClient.invalidateQueries({
          queryKey: [
            'viewStatus',
            variables.discussionOid,
            variables.userWallet,
          ],
        });
      }
    },
  });

  // Delete discussion mutation
  const deleteDiscussion = useMutation({
    mutationFn: async ({ discussionOid }) => {
      const response = await axios.delete(
        API_ENDPOINTS.FORUM.DISCUSSIONS.DELETE_DISCUSSION(discussionOid)
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['forum', 'discussions']);
      queryClient.invalidateQueries([
        'forum',
        'discussion',
        variables.discussionOid,
      ]);
    },
  });

  // Restore discussion mutation (within 5-minute window)
  const restoreDiscussion = useMutation({
    mutationFn: async ({ discussionOid }) => {
      const response = await axios.put(
        API_ENDPOINTS.FORUM.DISCUSSIONS.RESTORE_DISCUSSION(discussionOid)
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(['forum', 'discussions']);
      queryClient.invalidateQueries([
        'forum',
        'discussion',
        variables.discussionOid,
      ]);
    },
  });

  return {
    useGetAllDiscussions,
    useGetDiscussionByOid,
    useGetCommentsByDiscussion,
    useGetCategories,
    useGetCategoryBySlug,
    useGetDiscussionsByCategory,
    useGetTrendingDiscussions,
    createDiscussion: () => createDiscussionMutation,
    addComment: () => addCommentMutation,
    upvoteInteraction: () => upvoteInteraction,
    downvoteInteraction: () => downvoteInteraction,
    freezeDiscussion: () => freezeDiscussion,
    unfreezeDiscussion: () => unfreezeDiscussion,
    addView: () => addView,
    deleteDiscussion: () => deleteDiscussion,
    restoreDiscussion: () => restoreDiscussion,
  };
}

export const useGetCategories = () => {
  const { useGetCategories } = useForum();
  const { data, isLoading, error } = useGetCategories();
  return { data, isLoading, error };
};
